// NodeFormModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './NodeFormModal.css';

// Components
import Modal from 'components/modal/Modal';
import FormRenderer from 'form/FormRenderer';
import NodeDetails from 'node/details/NodeDetails';

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * NodeFormModal
 *
 * Large modal for editing a single form template from Peek.
 *
 * @returns {ReactNode}
 */
const NodeFormModal = React.memo(({
  isOpen = false,
  form = null,
  node = null,
  onClose,
  onChange,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  const title = normalizeString(form?.title) || 'Untitled Form';
  const formKey = normalizeString(form?.key);

  const hasPersistedLayout = Boolean(
    form?.layout
    && form?.blocksByKey
    && Object.keys(form.blocksByKey).length,
  );

  if (!formKey) {
    return null;
  }

  // ----- RENDER ----- //

  return (
    <Modal
      isOpen={isOpen}
      title={title}
      width={isMobile ? '100%' : 'min(960px, 94vw)'}
      height={isMobile ? '100%' : 'min(900px, 92vh)'}
      maxHeight={isMobile ? '100%' : '92vh'}
      padding="0"
      onClose={onClose}
    >
      <div className="node-form-modal-body">
        <div className="node-form-modal-surface">
          {hasPersistedLayout ? (
            <FormRenderer
              formDoc={form}
              node={node}
              onChange={onChange}
              onCoverSaved={onCoverSaved}
              onLocationSaved={onLocationSaved}
              onSubtitleSaved={onSubtitleSaved}
              onTagsSaved={onTagsSaved}
            />
          ) : (
            <NodeDetails
              node={node}
              onCoverSaved={onCoverSaved}
              onLocationSaved={onLocationSaved}
              onNodeChange={onChange}
              onSubtitleSaved={onSubtitleSaved}
              onTagsSaved={onTagsSaved}
            />
          )}
        </div>
      </div>
    </Modal>
  );
});

NodeFormModal.displayName = 'NodeFormModal';

export default NodeFormModal;
